/**
 * The page itself. One function per section, mirroring the sections of the
 * design source, assembled by `renderPage` for whichever language is current.
 */

import { h } from '../lib/dom.js';
import { CONTENT, LANGS, LANG_LABEL, OFFICE } from '../content.js';
import { BUSINESS, LEGAL, PAGES } from '../legal.js';
import { pathFor } from '../lib/router.js';
import { renderForm } from './form.js';

// Served straight from public/ so index.html's social-preview tags and the
// deploy script's per-path cache headers can both name a stable URL.
const wordmark = '/assets/veni-wordmark.svg';
const portrait = '/assets/portrait.webp';

export function renderPage(lang, { onLangChange, onNavigate }) {
  const c = CONTENT[lang];
  return h(
    'div',
    { class: 'page', dataset: { lang } },
    h('a', { class: 'skip-link', href: '#main' }, 'Skip to content'),
    nav(lang, c, onLangChange),
    h(
      'main',
      { class: 'shell', id: 'main' },
      hero(c),
      h('hr', { class: 'hr', style: 'margin: 0' }),
      territories(c),
      h('hr', { class: 'hr', style: 'margin: 0' }),
      services(c),
      about(c),
      h('hr', { class: 'hr', style: 'margin: 0' }),
      engage(lang, c, onNavigate),
      h('hr', { class: 'hr', style: 'margin: 0' }),
      contact(c),
      write(lang, c),
      h('hr', { class: 'hr', style: 'margin: 56px 0 0' }),
      footer(lang, c, onNavigate),
    ),
  );
}

function nav(lang, c, onLangChange) {
  return h(
    'nav',
    { class: 'nav' },
    h('span', { class: 'nav-brand' }, h('img', { src: wordmark, alt: 'Veni', width: 120, height: 22 })),
    h('a', { class: 'nav-link', href: '#services' }, c.nav.services),
    h('a', { class: 'nav-link', href: '#about' }, c.nav.about),
    h('a', { class: 'nav-link', href: '#fees' }, c.nav.fees),
    h('a', { class: 'nav-link', href: '#contact' }, c.nav.contact),
    h(
      'span',
      { class: 'lang-switch', role: 'group', 'aria-label': 'Language' },
      LANGS.map((l) =>
        h(
          'button',
          {
            type: 'button',
            class: 'lang-btn',
            lang: l,
            dataset: { lang: l },
            'aria-current': l === lang ? 'true' : 'false',
            onClick: () => onLangChange(l),
          },
          LANG_LABEL[l],
        ),
      ),
    ),
  );
}

function hero(c) {
  return h(
    'section',
    { class: 'hero' },
    h('h1', { class: 'hero-title' }, c.hero.lines.map((line) => h('span', null, line))),
    h('p', { class: 'hero-lead' }, c.hero.lead),
    h(
      'div',
      { class: 'actions' },
      h('a', { class: 'btn btn-primary', href: '#services' }, c.hero.primary),
      h('a', { class: 'btn btn-ghost', href: '#contact' }, c.hero.ghost),
    ),
  );
}

function territories(c) {
  return h(
    'section',
    { class: 'territories' },
    c.territories.places.map((place) => h('span', null, place)),
    h('span', { class: 'years' }, c.territories.years),
  );
}

function services(c) {
  return h(
    'section',
    { class: 'services', id: 'services' },
    h('span', { class: 'kicker' }, c.services.kicker),
    h('h2', { class: 'services-title' }, c.services.title),
    h(
      'div',
      { class: 'services-grid' },
      c.services.items.map((item, i) =>
        h(
          'div',
          { class: 'service' },
          h('span', { class: 'service-num', 'aria-hidden': 'true' }, String(i + 1).padStart(2, '0')),
          h('h3', null, item.title),
          h('p', null, item.body),
        ),
      ),
    ),
  );
}

/**
 * How an engagement is priced and paid for. Anyone deciding whether to write to
 * us wants this before they do; a card acquirer reviewing the site requires it
 * to be here at all.
 */
function engage(lang, c, onNavigate) {
  const e = c.engage;
  const l = LEGAL[lang];
  return h(
    'section',
    { class: 'engage', id: 'fees' },
    h('span', { class: 'kicker' }, e.kicker),
    h('h2', { class: 'engage-title' }, e.title),
    h('p', { class: 'engage-lead' }, e.lead),
    h(
      'div',
      { class: 'engage-grid' },
      e.items.map(([title, body]) =>
        h('div', { class: 'engage-item' }, h('h3', null, title), h('p', null, body)),
      ),
    ),
    h(
      'p',
      { class: 'engage-note' },
      e.note,
      ' ',
      ...['terms', 'refunds'].flatMap((page, i) => [
        i ? ' · ' : '',
        h(
          'a',
          {
            href: pathFor(lang, page),
            onClick: (e2) => { e2.preventDefault(); onNavigate(lang, page); },
          },
          l.nav[page],
        ),
      ]),
    ),
  );
}

function about(c) {
  return h(
    'section',
    { class: 'about', id: 'about' },
    h(
      'div',
      null,
      h('span', { class: 'kicker' }, c.about.kicker),
      h('h2', { class: 'about-title' }, c.about.title),
      h('p', null, c.about.body),
    ),
    h(
      'figure',
      { class: 'plate portrait' },
      h('img', { src: portrait, alt: c.about.portraitAlt, width: 445, height: 556, loading: 'lazy', decoding: 'async' }),
    ),
  );
}

function contact(c) {
  const block = (label, ...body) =>
    h('div', null, h('span', { class: 'contact-label' }, label), ...body);

  return h(
    'section',
    { class: 'contact', id: 'contact' },
    h('span', { class: 'kicker' }, c.contact.kicker),
    h('h2', { class: 'contact-title' }, OFFICE.legalName),
    h(
      'div',
      { class: 'contact-grid' },
      block(
        c.contact.phone,
        h(
          'p',
          { class: 'tnum' },
          OFFICE.phones.flatMap((line, i) => (i ? [h('br'), line] : [line])),
        ),
      ),
      block(c.contact.hk, h('p', null, OFFICE.hkAddress)),
      block(c.contact.macau, h('p', { class: 'tnum' }, OFFICE.macauAddress)),
      block(c.contact.web, h('p', null, h('a', { href: OFFICE.web.href }, OFFICE.web.label))),
    ),
  );
}

function write(lang, c) {
  return h(
    'section',
    { class: 'write' },
    h('div', null, h('h3', { class: 'write-title' }, c.write.title), h('p', { class: 'write-body' }, c.write.body)),
    renderForm(lang),
  );
}

function footer(lang, c, onNavigate) {
  const l = LEGAL[lang];
  return h(
    'footer',
    { class: 'footer' },
    h('span', null, c.footer.company),
    // The registration number belongs somewhere a card acquirer can see it
    // without opening a policy page, and it is the sort of thing a client
    // checking who they are paying looks for too.
    h('span', { class: 'footer-reg' }, `BR ${BUSINESS.registration}`),
    h(
      'span',
      { class: 'footer-legal' },
      ...PAGES.map((page) =>
        h(
          'a',
          {
            class: 'footer-link',
            // A real href, so the link is right-clickable and crawlable; the
            // handler only saves a reload.
            href: pathFor(lang, page),
            onClick: (e) => { e.preventDefault(); onNavigate(lang, page); },
          },
          l.nav[page],
        ),
      ),
    ),
    h('span', { class: 'places' }, c.footer.places),
  );
}
